import React, { useState, useMemo } from 'react';
import { BUILTIN_LISTS } from '../../config/lists';
import { normalizeName } from '../../logic/names';
import { unknownList } from '../../logic/lists';
import { Modal } from '../shared/Modal';

/**
 * ClosingSortSheet — "✨ Sort into lists". Reviews which Today list each closing item
 * feeds, with a suggestion from its name (logic/categorize.js). Two views:
 *   • Suggested — only items whose name says they're in the wrong list (and that the
 *     user hasn't already decided on), pre-ticked. Untick = "keep it where it is" (never
 *     suggested again); picking another list moves it there.
 *   • All items — every item with its list, searchable, for hand-fixing anything.
 * Nothing is written until "Apply". onApply({ moves: Map<id, list>, keep: Map<id, bool> })
 * — `keep` marks hand-picked lists (true) or accepted suggestions (false).
 *
 * rows: [{ id, name, path, current, kept, suggestion: { id, match } | null }]
 * lists / catById / short — the board's lists and their names (built-ins by default).
 */
const builtinById = id => BUILTIN_LISTS.find(c => c.id === id) || unknownList(id);

export function ClosingSortSheet({ rows, onApply, onClose, t, ln, lists = BUILTIN_LISTS, catById = builtinById, short }) {
  const nameOf = id => (short ? short(id) : t(`catShort.${id}`));
  // Frozen when the sheet opens, so rows don't jump around while you're deciding.
  const [suggested] = useState(() => rows.filter(r => !r.kept && r.suggestion && r.suggestion.id !== r.current));
  const [draft, setDraft] = useState(() => new Map(suggested.map(r => [r.id, r.suggestion.id])));
  const [touched, setTouched] = useState(() => new Set());   // rows the user changed in "All items"
  const [view, setView] = useState(suggested.length ? 'suggested' : 'all');
  const [q, setQ] = useState('');

  const byId = useMemo(() => new Map(rows.map(r => [r.id, r])), [rows]);
  const valueOf = r => draft.get(r.id) || r.current;
  const setTo = (r, id) => {
    setDraft(d => { const n = new Map(d); if (id === r.current) n.delete(r.id); else n.set(r.id, id); return n; });
    setTouched(s => new Set(s).add(r.id));
  };
  const toggle = r => (draft.has(r.id) ? setTo(r, r.current) : setTo(r, r.suggestion.id));

  // Moves = rows whose list changes. Keep decisions: an unticked suggestion stays put and
  // is remembered (true); a hand-picked list other than the suggestion is remembered
  // (true); taking the suggestion clears any old decision (false).
  const changes = [...draft.entries()].filter(([id, cat]) => byId.has(id) && byId.get(id).current !== cat);
  const keep = new Map();
  suggested.forEach(r => { if (!draft.has(r.id)) keep.set(r.id, true); });
  touched.forEach(id => {
    const r = byId.get(id); if (!r) return;
    const val = draft.get(id) || r.current;
    if (!keep.has(id)) keep.set(id, !(r.suggestion && r.suggestion.id === val));
  });
  const declined = suggested.filter(r => !draft.has(r.id)).length;
  // Live tally of where the changes go ("🛒 Groceries +6 · 🔪 Prep +12").
  const tally = lists.map(c => [c, changes.filter(([, cat]) => cat === c.id).length]).filter(([, n]) => n);
  const canApply = changes.length > 0 || declined > 0 || touched.size > 0;
  // A stray tap outside must not throw away the user's own decisions (pre-ticked
  // suggestions don't count — reopening brings them back). Cancel / Esc still close.
  const onBackdrop = () => { if (!touched.size) onClose(); };

  const shown = useMemo(() => {
    if (view === 'suggested') return suggested;
    const k = normalizeName(q);
    return k ? rows.filter(r => normalizeName(`${r.name} ${ln(r.name)} ${r.path}`).includes(k)) : rows;
  }, [view, suggested, rows, q, ln]);

  // Group consecutive rows by their Section › Sub-section path (rows arrive in walk order).
  const groups = [];
  shown.forEach(r => { const g = groups[groups.length - 1]; if (g && g.path === r.path) g.rows.push(r); else groups.push({ path: r.path, rows: [r] }); });

  const apply = () => onApply({ moves: new Map(changes), keep });

  return (
    <Modal backdropClassName="do-sort-back" className="do-modal--wide do-sort" label={t('sort.title')}
      onClose={onClose} onBackdrop={onBackdrop} backdropProps={{ 'data-testid': 'sort-backdrop' }}>
      <div className="do-sort__head">
        <h2 className="do-print__title">{t('sort.title')}</h2>
        <p className="do-print__date">{t('sort.subtitle')}</p>
        <div className="do-sort__tabs" role="tablist">
          <button role="tab" aria-selected={view === 'suggested'} className={`do-sort__tab${view === 'suggested' ? ' do-sort__tab--on' : ''}`}
            onClick={() => setView('suggested')}>{t('sort.suggested')} <span className="do-sort__n">{suggested.length}</span></button>
          <button role="tab" aria-selected={view === 'all'} className={`do-sort__tab${view === 'all' ? ' do-sort__tab--on' : ''}`}
            onClick={() => setView('all')}>{t('sort.all')} <span className="do-sort__n">{rows.length}</span></button>
          {view === 'all' && (
            <input className="do-sort__search" value={q} onChange={e => setQ(e.target.value)} placeholder={t('sort.search')} aria-label={t('sort.search')} />
          )}
        </div>
      </div>

      <div className="do-sort__body">
        {shown.length === 0 && (
          <div className="do-sort__empty">
            {view === 'suggested' ? <>✓ {t('sort.allGood')}</> : t('sort.noMatch')}
          </div>
        )}
        {groups.map(g => (
          <div key={g.path} className="do-sort__grp">
            <div className="do-sort__path">{g.path}</div>
            {g.rows.map(r => {
              const val = valueOf(r);
              const moved = val !== r.current;
              const from = catById(r.current);
              const to = catById(val);
              return (
                <div key={r.id} className={`do-sort__row${moved ? ' do-sort__row--on' : ''}`} style={{ '--sc': to.color }}>
                  {view === 'suggested' ? (
                    <button className="do-sort__check" role="checkbox" aria-checked={moved} onClick={() => toggle(r)}
                      aria-label={t('sort.includeItem', { name: ln(r.name) })}>
                      <span className="do-cl__box">{moved ? '✓' : ''}</span>
                    </button>
                  ) : <span className="do-sort__dot" aria-hidden="true">{to.icon}</span>}
                  <div className="do-sort__name">
                    <span className="do-sort__label">
                      {ln(r.name)}
                      {r.kept && !touched.has(r.id) && <span className="do-sort__pin" title={t('sort.keptHint')}> 📌</span>}
                    </span>
                    {view === 'suggested' && !moved
                      ? <span className="do-sort__why">{t('sort.staysHint', { cat: nameOf(r.current) })}</span>
                      : r.suggestion && r.suggestion.id !== r.current && (
                        <span className="do-sort__why">{t('sort.because', { word: r.suggestion.match, cat: nameOf(r.suggestion.id) })}</span>
                      )}
                  </div>
                  {moved && <span className="do-sort__from" title={t('sort.wasIn')}>{from.icon} <s>{nameOf(from.id)}</s> →</span>}
                  <select className="do-sort__select" value={val} onChange={e => setTo(r, e.target.value)}
                    aria-label={t('sort.listFor', { name: ln(r.name) })}>
                    {(lists.some(c => c.id === val) ? lists : [...lists, catById(val)]).map(c => <option key={c.id} value={c.id}>{c.icon} {nameOf(c.id)}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="do-print__actions do-sort__foot">
        <div className="do-sort__tally">
          {tally.map(([c, n]) => <span key={c.id} className="do-sort__chip" style={{ '--sc': c.color }}>{c.icon} {nameOf(c.id)} +{n}</span>)}
          {declined > 0 && <span className="do-sort__chip do-sort__chip--keep">📌 {t('sort.keepingN', { n: declined })}</span>}
          {!tally.length && !declined && <span className="do-sort__hint">{t('sort.noChanges')}</span>}
          {changes.length > 0 && <span className="do-sort__hint">{t('sort.boardToo')}</span>}
        </div>
        <button className="do-modal__btn" onClick={onClose}>{t('common.cancel')}</button>
        <button className="do-modal__btn do-modal__btn--primary" onClick={apply} disabled={!canApply}>
          {changes.length ? t('sort.apply', { n: changes.length }) : t('sort.save')}
        </button>
      </div>
    </Modal>
  );
}
